import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  PAYMENT_SCHEDULE_SIGN_STATUS_DONE,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"
import PollingService from "redux/api/pollingService"
import {
  start as startVerification,
  reset as resetVerification,
} from "redux/features/verification/verificationSlice"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import { fetchDebts } from "redux/features/debt/debtThunk"
import { fetchPaymentSchedule } from "redux/features/payment_schedule/paymentScheduleThunk"
import { PaymentScheduleSignValues } from "types"

export const createPaymentScheduleSignSession = createAsyncThunk(
  "payment-schedule-sign-session/create",
  async (values: PaymentScheduleSignValues) => {
    const response = await API.post(
      `/payment_schedules/${values.payment_schedule_id}/sign`,
      values,
    )
    return response.data
  },
)

export const fetchPaymentScheduleSignSession = createAsyncThunk(
  "payment-schedule-sign-session/fetch",
  async (values: { payment_schedule_id: string; session_id: string }) => {
    const response = await API.get(
      `/payment_schedules/${values.payment_schedule_id}/sign_session/${values.session_id}`,
    )

    return response.data
  },
)

export const makeSignPaymentSchedule = (values: PaymentScheduleSignValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(createPaymentScheduleSignSession(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("notice.sign.session.unavailable"))
            }

            const { session_id, verification_code } = payload.sign
            dispatch(startVerification({ code: verification_code }))

            let sign_result: string

            const timerId = setInterval(async () => {
              const pollingId = PollingService.getPolling()

              if (!pollingId) {
                clearInterval(timerId)
                return reject(i18n.t("common.request_cancelled"))
              }

              const { payment_schedule_id } = values
              const result = await dispatch(
                fetchPaymentScheduleSignSession({
                  payment_schedule_id,
                  session_id,
                }),
              )

              if (
                result?.payload?.sign?.status ===
                PAYMENT_SCHEDULE_SIGN_STATUS_DONE
              ) {
                clearInterval(timerId)
                dispatch(resetPolling())

                dispatch(resetVerification())
                dispatch(fetchPaymentSchedule({ payment_schedule_id }))
                dispatch(fetchDebts())

                sign_result = "success"

                return resolve(sign_result)
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!sign_result) {
                reject(i18n.t("notice.payment_schedule.sign.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.sign.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
